import { AgxButton, AgxSpacer, AgxTable, useToast } from '@urbanx/agx.ui-components2';
import SupplierUpdateModal from './SupplierUpdateModal';
import { useEffect, useState } from 'react';
import useAgencySupplierTableConfig from './useAgencySupplierTableConfig';
import {
  AgencySupplier,
  AgencySuppliers,
  AgencySuppliers as AgencySuppliersType,
  useDeleteAgencySupplier,
  useUpdateAgencySuppliers,
} from '@urbanx/agx.agencies.client';
import MarketingSubpageToolbar from '../MarketingSubpageToolbar';

interface AgencySuppliersProps {
  agencySuppliers?: AgencySuppliersType;
  onSuppliersChanged?: () => void;
  onBack: () => void;
}

const AgxAgencySuppliers = ({
  agencySuppliers,
  onSuppliersChanged,
  onBack,
}: AgencySuppliersProps) => {
  const { pushToast } = useToast();
  const agencyId = agencySuppliers?.agencyId ?? '';
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [currentSuppliers, setCurrentSuppliers] = useState(agencySuppliers?.suppliers ?? []);
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1);
  const defaultSupplier = {
    supplierId: undefined,
    companyName: undefined,
    email: undefined,
    phone: undefined,
    notes: undefined,
  };

  useEffect(() => {
    setCurrentSuppliers(agencySuppliers?.suppliers ?? []);
  }, [agencySuppliers?.suppliers]);

  const { updateAgencySuppliers } = useUpdateAgencySuppliers({
    onSuccess: (updatedSuppliers?: AgencySuppliers) => {
      setShowUpdateModal(false);
      setEditMode(false);
      if (updatedSuppliers?.suppliers) {
        setCurrentSuppliers(updatedSuppliers.suppliers);
      }
      onSuppliersChanged?.();
      pushToast({
        type: 'success',
        message: 'Agency suppliers updated successfully',
      });
    },
    onError: () =>
      pushToast({
        type: 'error',
        message: 'Error updating agency suppliers',
      }),
  });

  const { deleteAgencySupplier } = useDeleteAgencySupplier({
    onSuccess: (updatedSuppliers?: AgencySuppliers) => {
      setShowUpdateModal(false);
      setEditMode(false);
      if (updatedSuppliers?.suppliers) setCurrentSuppliers(updatedSuppliers.suppliers);

      onSuppliersChanged?.();
      pushToast({
        type: 'success',
        message: 'Agency suppliers updated successfully',
      });
    },
    onError: () =>
      pushToast({
        type: 'error',
        message: 'Error updating agency suppliers',
      }),
  });

  const addSupplierClick = () => {
    setSelectedSupplierIndex(-1);
    setShowUpdateModal(true);
    setEditMode(false);
  };

  const editSupplierClick = (index: number) => {
    setSelectedSupplierIndex(index);
    setShowUpdateModal(true);
    setEditMode(true);
  };

  const updateModal = (
    <SupplierUpdateModal
      isEditMode={isEditMode}
      updateableData={isEditMode ? currentSuppliers[selectedSupplierIndex] : defaultSupplier}
      onSave={(supplier: AgencySupplier) => {
        updateAgencySuppliers({
          ...supplier,
          agencyId,
        });
      }}
      onDelete={(supplierId: string | null) =>
        deleteAgencySupplier({
          agencyId,
          supplierId: supplierId ?? undefined,
        })
      }
      onClose={() => setShowUpdateModal(false)}
    />
  );

  const [supplierTableConfig, supplierRows] = useAgencySupplierTableConfig(
    currentSuppliers,
    editSupplierClick
  );

  if (!agencySuppliers) return null;

  return (
    <AgxSpacer direction="vertical" fill>
      <MarketingSubpageToolbar backButtonId="marketing-suppliers-back" onBack={onBack}>
        <AgxButton
          text="Add Supplier"
          id="add-supplier"
          variant="primary"
          size="small"
          onClick={addSupplierClick}
        />
      </MarketingSubpageToolbar>
      {showUpdateModal && updateModal}
      <AgxTable
        key="agency-suppliers-table"
        columns={supplierTableConfig}
        rows={supplierRows}
        tableStyle="square"
      />
    </AgxSpacer>
  );
};

export default AgxAgencySuppliers;
